import { Icons } from '@cast/design-system';
import { usePath } from '@cast/utils';

import { useIsClusterDemoMode } from '@cast/infrastructure-demo';
import { PRODUCT_BASE_PATHS } from '@cast/console-constants';
import { useAbility } from '@cast/shared-ability-data-access';
import {
  getOrgSecurityRoutePath,
  makeOrgCompliancePath,
  makeOrgVulnerabilityManagementPath,
} from 'features/organization/security-insights';
import { useHasNoAgentsEnabled } from 'features/organization/security-insights/hooks/useHasNoAgentsEnabled';
import { useIsSecuritySunsetEnabled } from 'features/organization/security-insights/hooks/useIsSecuritySunsetEnabled';
import { NewLabel } from 'main-layout/sidebar/_components/NewLabel';

import { CAN_VIEW_SECURITY } from '../../../../../constants';
import { SidebarItem } from '../_components/SidebarItem';
import { SidebarLayout } from '../_components/SidebarLayout';
import { SidebarOrgSection } from '../_components/SidebarOrgSection';

export const SecuritySidebar = () => {
  const isSecuritySunsetEnabled = useIsSecuritySunsetEnabled();
  const basePath = PRODUCT_BASE_PATHS.security;
  const orgComplianceLink = usePath(makeOrgCompliancePath);
  const orgVulnerabilityManagementLink = usePath(
    makeOrgVulnerabilityManagementPath
  );
  const isClusterDemoMode = useIsClusterDemoMode();

  const canViewSecurityRuntime = useAbility().can(
    'view',
    'OrganizationSecurityRuntime'
  );
  const { hasNoAgentsEnabled, isLoading: isAgentsLoading } =
    useHasNoAgentsEnabled();
  const securityNotEnabled = !isAgentsLoading && hasNoAgentsEnabled;

  if (isSecuritySunsetEnabled) {
    return null;
  }

  return (
    <SidebarLayout
      title="Security"
      subtitle="Scan & remediate"
      icon={<Icons.CastSecurity variant="preset" weight="light" size={24} />}
    >
      <SidebarOrgSection>
        {(isClusterDemoMode || CAN_VIEW_SECURITY()) && (
          <SidebarItem
            title="Overview"
            url={`${basePath}/dashboard`}
            allowedInReadOnlyOrg
          />
        )}

        <SidebarItem
          title="Compliance"
          url={orgComplianceLink}
          rules={[['view', 'OrganizationSecurityCompliance']]}
        />

        <SidebarItem
          url={orgVulnerabilityManagementLink}
          title="Vulnerabilities"
          rules={[['view', 'OrganizationSecurityVulnerabilities']]}
        />

        <SidebarItem
          url={getOrgSecurityRoutePath('attack-path')}
          title="Attack paths"
          rules={[['view', 'OrganizationSecurityAttackPaths']]}
        />

        {CAN_VIEW_SECURITY() && (
          <SidebarItem
            url={getOrgSecurityRoutePath('runtime')}
            title="Runtime"
            endAdornment={
              !canViewSecurityRuntime && !securityNotEnabled ? (
                <NewLabel />
              ) : undefined
            }
          />
        )}

        <SidebarItem
          url={getOrgSecurityRoutePath('workloads')}
          title="Workloads"
          rules={[['view', 'OrganizationSecurityWorkloads']]}
        />

        <SidebarItem
          url={getOrgSecurityRoutePath('node-updates')}
          title="Node updates"
          rules={[['view', 'OrganizationSecurityNodeRotation']]}
        />

        <SidebarItem
          url={getOrgSecurityRoutePath('settings')}
          title="Settings"
          rules={[['view', 'OrganizationSecuritySettings']]}
        />
      </SidebarOrgSection>
    </SidebarLayout>
  );
};
